import { useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';

import useSecuredApiV2 from './use-secured-api-v2';
import {
  type CustomerOrderDetailDto,
  toCustomerOrderDetail,
} from '../dto/customer-order/customer-order-detail-dto';
import { type ResponseDto } from '../dto/ResponseDto';
import { type CustomerOrder } from '../entities';
import { CUSTOMER_ORDER_END_POINT_URL } from '../utils/endpoints';
import type ProblemDetailsDto from '../dto/error/problem-details-dto';

export interface CustomerOrderDetailResult {
  data?: CustomerOrder;
  isLoading: boolean;
  errorMessage?: string;
  /** The status of the failed request, so callers can tell a missing order from any other failure */
  errorStatus?: number;
}

/**
 * Loads the customer order whose id "orderId" with the line items of its latest revision, from the customer order
 * controller
 */
const useCustomerOrderDetail = (orderId: string | undefined): CustomerOrderDetailResult => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<CustomerOrderDetailResult>({ isLoading: true });

  useEffect(() => {
    if (!orderId) {
      setState({ isLoading: false });
      return;
    }
    setState({ isLoading: true });

    const subscription = api
      .get(`${CUSTOMER_ORDER_END_POINT_URL}/${orderId}`, {}, {})
      .pipe(
        tap((response: ResponseDto<CustomerOrderDetailDto>) => {
          setState({ data: toCustomerOrderDetail(response.data), isLoading: false });
        }),
        catchError((error: ProblemDetailsDto) => {
          setState({
            isLoading: false,
            errorMessage: error.detail ?? 'Failed to load the order',
            errorStatus: error.status,
          });
          return EMPTY;
        })
      )
      .subscribe();

    // Ignore the response if the order changes or the component unmounts before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, [orderId]);

  return state;
};

export default useCustomerOrderDetail;
